/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import ButtonLoading from "../../../../../components/Button/ButtonLoading";
import { Box, Button, Paper, TextField } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import { apiPost } from "../../../../../services/apiClient";
import Grid from "@mui/material/Grid";
import flexAiChatAgentStyle from "./FlexAiChatAgentStyle";
import { queryKeys } from "../../../../../services/reactQuery/reactQueryService";
import { getCaseLink } from "../../../../../services/productService";
import { getFlexLpConfigByAiAnswer } from "../../../../../services/advocardService";
import { useCustomer } from "../../../../../provider/Customer/CustomerProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "../../../../../services/landingPagesList";
import FlexAiChatAgentCTAButton from "./FlexAiChatAgentCTAButton";
import { AdvocardFlexAiResponse } from "../../../../../types/AdvocardFlexAiResponse";
import { CreateFlexCaseRequest } from "../../../../../components/Checkin/AdvocardFlexCheckin/AdvocardFlexCheckin";
import { gaEvent } from "../../../../../services/googleAnalyticsService";
import dayjs from "dayjs";
import { Link } from "react-router";

type FlexAiChatAgentProps = {
  postalCode: string;
  email: string;
  insurancePolicyNumber: string;
  aiResponse: AdvocardFlexAiResponse | null;
  setAiResponse: Function;
  setCreateCaseRequest: Function;
  questionLabel: string;
  placeholder: string;
};

const FlexAiChatAgent = ({
  email,
  postalCode,
  insurancePolicyNumber,
  aiResponse,
  setAiResponse,
  setCreateCaseRequest,
  questionLabel,
  placeholder = "Geben Sie hier Ihre Frage ein",
}: FlexAiChatAgentProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [downloadIsLoading, setDownloadIsLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const { isLoggedIn } = useCustomer();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const aiResponseIsSuccess = aiResponse?.status == "success";
  const isQuestionCurrentFlexServiceRelated = aiResponse?.isQuestionCurrentFlexServiceRelated;

  const handleSubmit = async () => {
    setIsLoading(true);

    gaEvent({
      category: "FlexService",
      action: "FlexService AI-Button Click",
      label: dayjs().format("DD.MM.YYYY, HH:mm"),
    });

    try {
      const response: AdvocardFlexAiResponse = await apiPost("/advocard/flex/send_ai_request", {
        question: question,
        postalCode,
        insurancePolicyNumber,
      });

      setIsLoading(false);
      setAiResponse(response);

      if (response.isQuestionCurrentFlexServiceRelated) {
        setCreateCaseRequest((prevState: CreateFlexCaseRequest) => {
          return { ...prevState, customerQuestion: question, isQuestionCurrentFlexServiceRelated: true };
        });
      }

      const predictedLegalProduct = response.predictedLegalProduct;

      if (predictedLegalProduct && predictedLegalProduct !== "nonSupportedProduct") {
        setCreateCaseRequest((prevState: CreateFlexCaseRequest) => {
          return { ...prevState, customerQuestion: question, productClassName: predictedLegalProduct };
        });
      }
    } catch (e: unknown) {
      setIsLoading(false);
    }
  };

  const handleNewQuestion = () => {
    setQuestion("");
    setAiResponse(null);
  };

  const getProductFromAiResponse = (aiResponse: AdvocardFlexAiResponse) => {
    if (aiResponse.predictedLabel === "currentFlexServiceRelatedQuestionTravel") {
      return "travelContract";
    }

    if (aiResponse.predictedLabel === "currentFlexServiceRelatedQuestionProvider") {
      return "providerContract";
    }

    return aiResponse.predictedLegalProduct;
  };

  const createCase = async () => {
    const predictedLegalProduct = aiResponse ? getProductFromAiResponse(aiResponse) : null;

    if (!aiResponse || !predictedLegalProduct || aiResponse.predictedLegalProduct === "nonSupportedProduct") {
      return;
    }

    setIsLoading(true);

    const result = await apiPost("/advocard/flex/create_case", {
      email: email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      productClassName: predictedLegalProduct,
      affiliateCode: localStorage.getItem("gclid"),
      postalCode: postalCode,
      insurancePolicyNumber: insurancePolicyNumber,
      customerQuestion: question,
      landingPage: (getLpByLocation(location.pathname) || { keyword: location.pathname }).keyword,
      isQuestionCurrentFlexServiceRelated: isQuestionCurrentFlexServiceRelated,
    });

    if (!isLoggedIn) {
      localStorage.setItem("token", result.token);
      gaEvent({
        category: "Registration",
        action: "Account created",
      });
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });

    navigate(getCaseLink(result.case) + "/antrag");
  };

  const handleDownload = async () => {
    if (!aiResponseIsSuccess) return;

    try {
      setDownloadIsLoading(true);

      const response = await apiPost(
        "/services/generate_advocard_flex_ai_response_pdf",
        {
          question,
          response: aiResponse.answer,
          postalCode,
          insurancePolicyNumber,
        },
        undefined,
        false
      );

      if (response.status !== 200) {
        return setDownloadIsLoading(false);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.headers.get("Content-Disposition")?.match(/=(.+)/)?.[1] || "Flexservice_Frage_" + dayjs().format("DDMMYYYY") + ".pdf";
      a.hidden = true;
      a.click();

      setDownloadIsLoading(false);
    } catch (e: unknown) {
      setDownloadIsLoading(false);
    }
  };

  return (
    <>
      {aiResponse && aiResponse.status === "success" && (
        <>
          <Paper elevation={4} sx={{ p: 3, my: 3 }}>
            <Typography align={"left"}>
              <strong>{questionLabel}</strong>
              <br />
              {question}
            </Typography>
            <Typography align={"left"} sx={{ mt: 3 }}>
              <strong>Unsere Antwort:</strong>
              <br />
            </Typography>
            <Typography align={"left"} dangerouslySetInnerHTML={{ __html: getFlexLpConfigByAiAnswer(aiResponse, createCase, isLoading).textToShow }} />
          </Paper>
          <Grid
            container
            spacing={3}
            direction={
              isQuestionCurrentFlexServiceRelated || (aiResponse.predictedLegalProduct && aiResponse.predictedLegalProduct !== "nonSupportedProduct")
                ? "row-reverse"
                : "row"
            }
          >
            <Grid
              size={{
                xs: 12,
                md: isQuestionCurrentFlexServiceRelated ? 4 : 6
              }}>
              <Button component={Link} to={"#frage-stellen"} onClick={handleNewQuestion} sx={flexAiChatAgentStyle.button} fullWidth={true}>
                Weitere Frage stellen
              </Button>
            </Grid>
            {isQuestionCurrentFlexServiceRelated && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <ButtonLoading
                  variant={"contained"}
                  disabled={!aiResponseIsSuccess}
                  onClick={handleDownload}
                  isLoading={downloadIsLoading}
                  loadingText={"Pdf wird generiert"}
                  sx={{ px: 3, width: "100%" }}
                >
                  Antwort als Pdf speichern
                </ButtonLoading>
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                md: isQuestionCurrentFlexServiceRelated ? 4 : 6
              }}>
              <FlexAiChatAgentCTAButton aiResponse={aiResponse} createCase={createCase} isLoading={isLoading} />
            </Grid>
          </Grid>
        </>
      )}
      {(!aiResponse || (aiResponse && aiResponse.status === "error")) && (
        <>
          <Box id={"frage-stellen"} sx={{ position: "relative", top: "-200px" }} />
          <Grid
            container
            wrap="wrap"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              mt: 3
            }}>
            <Grid size={12}>
              <TextField
                placeholder={placeholder}
                aria-label={questionLabel}
                multiline
                value={question}
                onChange={(e) => setQuestion(e.target.value.substring(0, 3000))}
                rows={10}
                fullWidth
                helperText={"[" + question.length + "/3000]"}
                disabled={isLoading}
              />
            </Grid>
            {aiResponse?.status === "error" && (
              <Grid
                size={12}
                sx={{
                  textAlign: "center",
                  mt: 3
                }}>
                <Typography sx={{ mt: 3, color: "error.main" }}>
                  Ihre Anfrage konnte leider nicht verarbeitet werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt noch einmal.
                </Typography>
              </Grid>
            )}
            <Grid
              size={12}
              sx={{
                textAlign: "center",
                mt: 3
              }}>
              <ButtonLoading
                variant={"contained"}
                disabled={question.length === 0}
                onClick={handleSubmit}
                isLoading={isLoading}
                loadingText={"Antwort wird generiert"}
                endIcon={<AutoAwesome />}
                sx={{ px: 3 }}
              >
                Antwort erhalten
              </ButtonLoading>
            </Grid>
            <Grid
              size={12}
              sx={{
                textAlign: "center",
                mt: 3
              }}>
              <Typography variant={"caption"} color={"textSecondary"} sx={{
                fontSize: "small"
              }}>
                Mit dem Klick auf “Antwort erhalten” erklären Sie sich damit einverstanden, dass Ihre Eingabe zur automatisierten Beantwortung verarbeitet wird.
                Bitte geben Sie keine personenbezogenen Daten ein. Weitere Informationen finden Sie in unserer{" "}
                <Link to={"/datenschutz"} target="_blank">
                  {" "}
                  Datenschutzerklärung
                </Link>{" "}
                .
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default FlexAiChatAgent;
